import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { CustomerForm } from "@/components/pro/CustomerForm";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { fullName } from "@/lib/format";
import { t } from "@/i18n";

const portalStatus = (c) => (c.has_portal_account ? "active" : c.portal_status === "invited" ? "invited" : "none");

export default function Customers() {
  const navigate = useNavigate();
  const { readOnly } = useGarage();
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const [archived, setArchived] = useState(false);
  const { data, error, loading, reload } = useFetch("/customers", { q: query, archived });

  const addBtn = !readOnly && (
    <Button onClick={() => setOpen(true)} data-testid="new-customer-button"><Plus className="mr-2 h-4 w-4" /> {t("customers.new")}</Button>
  );

  return (
    <div>
      <PageHeader title={t("pro.nav.customers")} subtitle={t("customers.subtitle")} actions={addBtn} />
      <form onSubmit={(e) => { e.preventDefault(); setQuery(q); }} className="mb-6 flex max-w-2xl flex-wrap items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("customers.search")} className="pl-9" data-testid="customer-search-input" />
        </div>
        <Button type="submit" variant="outline" data-testid="customer-search-button">{t("common.search")}</Button>
        <label className="ml-2 flex items-center gap-2 whitespace-nowrap text-sm text-slate-700"><Switch checked={archived} onCheckedChange={setArchived} data-testid="show-archived-switch" /> {t("archive.showArchived")}</label>
      </form>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && (
        <EmptyState icon={Users} title={archived ? t("archive.noneArchived") : query ? t("customers.noResults") : t("customers.empty")} text={query ? "" : t("customers.emptyText")} action={!query && addBtn} testId="customers-empty-state" />
      )}
      {data && data.length > 0 && (
        <>
          <div className="gf-card hidden overflow-x-auto md:block">
            <Table data-testid="customers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>{t("customers.name")}</TableHead>
                  <TableHead>{t("auth.email")}</TableHead>
                  <TableHead>{t("customers.phone")}</TableHead>
                  <TableHead>{t("pro.nav.vehicles")}</TableHead>
                  <TableHead>{t("customers.portal")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/pro/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-semibold text-slate-900">{fullName(c)}</TableCell>
                    <TableCell className="text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-sm">{c.phone || "—"}</TableCell>
                    <TableCell>{c.vehicles_count}</TableCell>
                    <TableCell><StatusBadge status={portalStatus(c)} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="grid gap-3 md:hidden">
            {data.map((c) => (
              <button key={c.id} type="button" onClick={() => navigate(`/pro/customers/${c.id}`)} className="gf-card p-4 text-left" data-testid={`customer-card-${c.id}`}>
                <div className="flex items-center justify-between"><p className="font-semibold">{fullName(c)}</p><StatusBadge status={portalStatus(c)} /></div>
                <p className="mt-1 text-sm text-slate-500">{c.email || c.phone || "—"} · {t("customers.vehiclesCount", { n: c.vehicles_count })}</p>
              </button>
            ))}
          </div>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="new-customer-dialog">
          <DialogHeader>
            <DialogTitle>{t("customers.new")}</DialogTitle>
            <DialogDescription>{t("customers.newHint")}</DialogDescription>
          </DialogHeader>
          {open && <CustomerForm onSaved={(c) => { setOpen(false); toast.success(t("customers.created")); navigate(`/pro/customers/${c.id}`); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { portalStatus };
